// INP OPTIMIZED VERSION
import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Ultra optimized Variant Picker
 *
 * Main goals:
 * - Reduce INP
 * - Reduce main-thread blocking
 * - Reduce large DOM morphs
 * - Prevent unnecessary reflows
 * - Abort stale requests
 * - Schedule heavy work outside interaction frame
 */

export default class VariantPicker extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {string | undefined} */
  #pendingRequestUrl;

  connectedCallback() {
    super.connectedCallback();

    this.onVariantChange = this.variantChanged.bind(this);

    this.addEventListener('change', this.onVariantChange, {
      passive: true,
    });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onVariantChange);
  }

  /**
   * Variant change handler
   * @param {Event} event
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const target = event.target;

    this.updateSelectedOption(target);

    this.dispatchEvent(
      new VariantSelectedEvent({
        id: target.dataset.optionValueId ?? '',
      })
    );

    const isOnProductPage =
      Theme.template.name === 'product' &&
      !target.closest('product-card') &&
      !target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = target.dataset.connectedProductUrl;

    const loadsNewProduct =
      isOnProductPage &&
      !!newUrl &&
      newUrl !== currentUrl;

    const requestUrl = this.buildRequestUrl(target);

    // Update URL immediately (cheap task)
    this.updateBrowserUrl(target, loadsNewProduct, newUrl);

    // Defer fetch slightly to unblock interaction paint
    requestAnimationFrame(() => {
      this.fetchUpdatedSection({
        requestUrl,
        shouldMorphMain: loadsNewProduct,
      });
    });
  }

  /**
   * Optimized fetch/update pipeline
   */
  async fetchUpdatedSection({
    requestUrl,
    shouldMorphMain = false,
  }) {
    try {
      // Abort previous request
      this.#abortController?.abort();

      this.#abortController = new AbortController();

      const response = await fetch(requestUrl, {
        signal: this.#abortController.signal,
      });

      const responseText = await response.text();

      this.#pendingRequestUrl = undefined;

      // Yield back to browser before parsing
      await this.nextFrame();

      const html = new DOMParser().parseFromString(
        responseText,
        'text/html'
      );

      html
        .querySelector('overflow-list[defer]')
        ?.removeAttribute('defer');

      const textContent = html.querySelector(
        'variant-picker script[type="application/json"]'
      )?.textContent;

      if (!textContent) return;

      // Yield again before DOM mutations
      await this.nextFrame();

      if (shouldMorphMain) {
        this.updateCriticalSections(html);
      } else {
        this.updateVariantPicker(html);
      }

      // Dispatch event after paint-sensitive work
      if (this.selectedOptionId) {
        this.dispatchEvent(
          new VariantUpdateEvent(
            JSON.parse(textContent),
            this.selectedOptionId,
            {
              html,
              productId: this.dataset.productId ?? '',
            }
          )
        );
      }

      // Update buttons separately
      this.updateAddToCartButtons(html);

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error);
      }
    }
  }

  /**
   * Only morph critical sections
   * Avoid full-page morphing
   */
  updateCriticalSections(newHtml) {
    const selectors = [
      '.product-gallery',
      '.product-info',
      '.product-price',
      '.product-form',
      '.product-inventory',
    ];

    selectors.forEach((selector) => {
      const currentEl = document.querySelector(selector);
      const newEl = newHtml.querySelector(selector);

      if (currentEl && newEl) {
        morph(currentEl, newEl);
      }
    });
  }

  /**
   * Smaller morph scope for better INP
   */
  updateVariantPicker(newHtml) {
    const newVariantPicker = newHtml.querySelector(
      this.tagName.toLowerCase()
    );

    if (!newVariantPicker) return;

    // Update dataset only
    if (newVariantPicker instanceof HTMLElement) {
      this.dataset.productId =
        newVariantPicker.dataset.productId;

      this.dataset.productUrl =
        newVariantPicker.dataset.productUrl;
    }

    // Morph only options area
    const currentOptions = this.querySelector(
      '.variant-picker__options'
    );

    const newOptions = newVariantPicker.querySelector(
      '.variant-picker__options'
    );

    if (currentOptions && newOptions) {
      morph(currentOptions, newOptions);
    }
  }

  /**
   * Lightweight ATC update
   */
  updateAddToCartButtons(html) {
    requestAnimationFrame(() => {
      const ajaxButton = html.querySelector(
        '.product-details add-to-cart-component button'
      );

      const currentButton = document.querySelector(
        '.product-details add-to-cart-component button'
      );

      if (ajaxButton && currentButton) {
        currentButton.replaceWith(ajaxButton);
      }

      // Buy more buttons
      const ajaxButtons = html.querySelectorAll(
        '.product__buymore-list add-to-cart-component button'
      );

      const currentButtons = document.querySelectorAll(
        '.product__buymore-list add-to-cart-component button'
      );

      currentButtons.forEach((btn, i) => {
        if (ajaxButtons[i]) {
          btn.replaceWith(ajaxButtons[i]);
        }
      });
    });
  }

  /**
   * Browser URL update
   */
  updateBrowserUrl(target, loadsNewProduct, newUrl) {
    const url = new URL(window.location.href);

    if (Theme.template.name === 'product') {
      if (target.dataset.variantId) {
        url.searchParams.set(
          'variant',
          target.dataset.variantId
        );
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct && newUrl) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Yield to next frame
   */
  nextFrame() {
    return new Promise((resolve) => {
      requestAnimationFrame(resolve);
    });
  }

  /**
   * Update selected option
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      target = this.querySelector(
        `[data-option-value-id="${target}"]`
      );

      if (!target) return;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const selectedOption = Array.from(
        target.options
      ).find((option) => option.value === target.value);

      if (!selectedOption) return;

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      selectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Build request URL
   */
  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = []
  ) {
    let productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;

    this.#pendingRequestUrl = productUrl;

    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(
        `option_values=${this.selectedOptionsValues.join(',')}`
      );
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(
          `option_values=${sourceSelectedOptionsValues.join(',')}`
        );
      } else {
        params.push(
          `option_values=${selectedOption.dataset.optionValueId}`
        );
      }
    }

    if (
      this.closest('quick-add-component') ||
      this.closest('swatches-variant-picker-component')
    ) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }

      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Selected option
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      'select option[selected], fieldset input:checked'
    );

    if (
      !(
        selectedOption instanceof HTMLInputElement ||
        selectedOption instanceof HTMLOptionElement
      )
    ) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Selected option ID
   */
  get selectedOptionId() {
    const { selectedOption } = this;

    if (!selectedOption) return undefined;

    return selectedOption.dataset.optionValueId;
  }

  /**
   * Selected option values
   */
  get selectedOptionsValues() {
    return Array.from(
      this.querySelectorAll(
        'select option[selected], fieldset input:checked'
      )
    ).map((option) => option.dataset.optionValueId);
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define(
    'variant-picker',
    VariantPicker
  );
}